import { getModuleDef } from '@helpers/getAppModule';
import { expressionsService } from '@services/domain-factories/expressions';
import { formModelsService } from '@services/domain-factories/formModels';
import { formsService } from '@services/domain-factories/forms';
import { getTableItems } from '@services/domain-factories/tables/tableItems';
import { getTables } from '@services/domain-factories/tables/tableQueries';
import { collectionsService } from '@services/firebase/firestore/functionFactory';
import { accountStore } from '@stores/bootstrap-stores/account';
import { appUserStore } from '@stores/bootstrap-stores/user';
import { globalUserGroupsStore } from '@stores/bootstrap-stores/userGroups';
import { hasItems } from '@utils/vanillaJs';
import {
  Category,
  Expression,
  FormModel,
  Member,
  SurveyResponse,
  Table,
  TableForm,
  TableItem,
  Tag,
  UserGroup,
  WorkspaceForm
} from '@vincerosoft/assessfluence-types';
import { where } from 'firebase/firestore';

function workspaceFactory() {
  function resolve(suffix?: string) {
    const title = getModuleDef('workspace') + (suffix ? suffix : '');
    return title;
  }

  async function getTagsAndCategories(forms: WorkspaceForm[]) {
    const docIds = forms.reduce<{ [key: string]: string[] }>(
      (acc, form) => {
        acc.formIds.push(form.id);
        if (form.options?.category) {
          acc.catIds.push(form.options.category);
        }

        if (form.options?.tags) {
          acc.tagIds.push(...form.options.tags);
        }

        return acc;
      },
      { formIds: [], catIds: [], tagIds: [] }
    );

    const categories: Category[] = [];
    const tags: Tag[] = [];

    if (docIds.catIds.length > 0) {
      await collectionsService.categories.getDocsInBatches(
        ['id', 'in', docIds.catIds],
        [],
        (data) => categories.push(...data)
      );
    }

    if (docIds.tagIds.length > 0) {
      await collectionsService.tags.getDocsInBatches(['id', 'in', docIds.tagIds], [], (data) =>
        tags.push(...data)
      );
    }

    return { categories, tags };
  }

  async function loadWorkspaceData(onProgress: (forms: WorkspaceForm[]) => void) {
    const inFlightStore: Record<
      string,
      {
        form: WorkspaceForm;
        formModel: FormModel | null;
        responses: SurveyResponse[] | null;
        expressions: Expression[] | null;
        userGroups: UserGroup[];
        tableAssociatedData: {
          table: Table | null;
          items: TableItem[];
          tff: TableForm | null;
          formModel: FormModel | null;
        };
      }
    > = {};
    const userGroups = globalUserGroupsStore.getState().userGroups || [];

    const formGrants = accountStore.getState().resourceGrants?.forms || [];
    const formIds = formGrants.map((f) => f.resource_id);

    if (formIds.length === 0) {
      return inFlightStore;
    }

    await formsService.getFormsByIds<WorkspaceForm>(
      'workspaceFactory',
      formIds,
      where('intent', '==', 'workspace'),
      (data) => {
        data.forEach((form) => {
          if (form.intent === 'workspace') {
            inFlightStore[form.id] = {
              form,
              formModel: null,
              responses: null,
              expressions: null,
              userGroups: userGroups.filter(
                (ug) => hasItems(ug.form_ids) && ug.form_ids.includes(form.id)
              ),
              tableAssociatedData: {
                table: null,
                items: [],
                tff: null,
                formModel: null
              }
            };
          }
        });
        onProgress(Object.values(inFlightStore).map((entry) => entry.form));
      }
    );

    const formModels = await formModelsService.getFormModelsByIds(formIds);
    formModels.forEach((formModel) => {
      const formModelInferred = formModel as FormModel & { form_id: string };
      if (inFlightStore[formModelInferred.form_id]) {
        inFlightStore[formModelInferred.form_id].formModel = formModel;
      }
    });

    const expressions = await expressionsService.getRuntimeExpressionsBySourceIds(formIds);
    Object.values(inFlightStore).forEach((entry) => {
      entry.expressions = expressions;
    });

    const tffIds = Object.values(inFlightStore).reduce<string[]>((acc, entry) => {
      const form = entry.form;
      if (form.options?.linked_table_id) {
        acc.push(form.options.linked_table_id);
      }
      return acc;
    }, []);

    const tableFactoryForms: TableForm[] = [];
    const formModelIds: string[] = [];
    if (tffIds.length > 0) {
      await formsService.getFormsByIds<TableForm>('workspaceFactory', tffIds, undefined, (data) => {
        data.forEach((form) => {
          formModelIds.push(form.form_model_id);
          const associatedForm = Object.values(inFlightStore).find(
            (f) => f.form.options?.linked_table_id === form.id
          );
          inFlightStore[associatedForm!.form.id].tableAssociatedData = {
            table: null,
            items: [],
            tff: form,
            formModel: null
          };
          tableFactoryForms.push(form);
        });
      });

      const tablesByFormId = await getTables(tableFactoryForms);
      tableFactoryForms.forEach((form) => {
        const tableData = tablesByFormId.get(form.id);
        if (tableData) {
          inFlightStore[form.id].tableAssociatedData = {
            table: tableData,
            items: [],
            tff: form,
            formModel: null
          };
        }
      });

      const formModels = await formModelsService.getFormModelsByIds(formModelIds);
      formModels.forEach((formModel) => {
        const formModelInferred = formModel as FormModel & { form_id: string };
        const tableData = inFlightStore[formModelInferred.form_id].tableAssociatedData;
        if (tableData) {
          inFlightStore[formModelInferred.form_id].tableAssociatedData = {
            ...tableData,
            formModel
          };
        }
      });

      const tableItemsPromises = tableFactoryForms.map(async (tff) => {
        const tableData = inFlightStore[tff.id].tableAssociatedData;
        if (tableData && tableData.table) {
          const items = await getTableItems(tff, {
            table: tableData.table,
            returnOnlyKeyAndValue: true
          });
          inFlightStore[tff.id].tableAssociatedData = {
            ...tableData,
            items
          };
        }
      });
      await Promise.all(tableItemsPromises);
    }

    const responses: SurveyResponse[] = [];
    const currentMember = appUserStore.getState().currentMember;

    await collectionsService.responses.getDocsInBatches(
      ['form.id', 'in', formIds],
      [where('respondent.member_id', '==', currentMember!.id)],
      (data) => responses.push(...data)
    );

    // sort responses by created_at descending
    responses.sort((a, b) => new Date(b.created_at!).getTime() - new Date(a.created_at!).getTime());

    responses.forEach((response) => {
      const formId = response.form.id as string;
      if (inFlightStore[formId]) {
        inFlightStore[formId].responses = inFlightStore[formId].responses || [];
        inFlightStore[formId].responses!.push(response);
      }
    });

    return inFlightStore;
  }

  const getResponsesCount = async (member: Member) =>
    collectionsService.responses.getCountInBatches([
      'respondent.member_id',
      'in',
      [member.id, member.user_id]
    ]);

  const getRecentResponses = async (member: Member, formIds: string[], date: Date) => {
    const responsesInBatches = await collectionsService.responses.getDocsInBatches(
      ['form.id', 'in', formIds],
      [where('respondent.member_id', '==', member.id), where('created_at', '>=', date)],
      (data) => data
    );
    return responsesInBatches.flat();
  };

  return {
    resolve,
    loadWorkspaceData,
    getResponsesCount,
    getRecentResponses,
    getTagsAndCategories
  };
}

export const workspaceService = workspaceFactory();
